// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/AccessibilityStatement.tsx
// Version: v1.0.0
// Description: Accessibility Statement page — improved text based on estata.com original

import React from "react";
import { useBreakpoints } from "../hooks/Breakpoints.tsx";
import Container from "../hooks/Container.tsx";

const AccessibilityStatement: React.FC = () => {
  const { current } = useBreakpoints();
  const isDesktop = current === "desktop";
  const isTablet = current === "tablet";

  const titleSize = isDesktop ? "text-7xl" : isTablet ? "text-5xl" : "text-3xl";
  const h2Size = isDesktop ? "text-3xl" : isTablet ? "text-2xl" : "text-xl";
  const bodySize = isDesktop ? "text-lg" : isTablet ? "text-base" : "text-sm";

  return (
    <section className="bg-black text-white pt-[50px] pb-[50px] sm:pt-[75px] sm:pb-[75px] lg:pt-[100px] lg:pb-[100px]">
      <Container>
        <h1 className={`font-bold mb-8 ${titleSize}`}>Erklärung zur Barrierefreiheit</h1>

        <p className={`${bodySize} font-semibold mb-6 text-white/90`}>
          ESTATA ist davon überzeugt, dass Technologie allen Menschen gleichermaßen zugänglich sein muss.
          Der uneingeschränkte Zugang zu digitalen Angeboten legt den Grundstein für eine integrative Kultur,
          die es jedem ermöglicht, erfolgreich zu sein — unabhängig von individuellen Einschränkungen.
        </p>

        <p className={`${bodySize} text-white/80 mb-12`}>
          Aus dieser Überzeugung heraus gestalten wir unsere Website estata.com und unsere Plattform MyEST
          barrierefrei, damit sie einem möglichst breiten Publikum zugänglich sind.
        </p>

        {/* Unsere Maßnahmen */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>Unsere Maßnahmen</h2>
          <div className="h-px bg-white/20 mb-6" />

          <div className="space-y-6">
            <div>
              <h3 className={`${bodySize} font-semibold mb-2`}>Kontrast & Typografie</h3>
              <p className={`${bodySize} text-white/80`}>
                Bei der Entwicklung unserer Websitevorlagen achten wir besonders auf ausreichenden Farbkontrast,
                eine logische Reihenfolge der Überschriften sowie auf konsistente, konfigurierbare wiederkehrende Elemente.
              </p>
            </div>

            <div>
              <h3 className={`${bodySize} font-semibold mb-2`}>Kompatibilität mit assistiven Technologien</h3>
              <p className={`${bodySize} text-white/80`}>
                Wir programmieren unsere Vorlagen so, dass sie mit verschiedenen Ein- und Ausgabegeräten kompatibel sind —
                darunter Tastatursteuerung und Bildschirmlesegeräte (Screen Reader).
              </p>
            </div>

            <div>
              <h3 className={`${bodySize} font-semibold mb-2`}>Alternativtexte & Medien</h3>
              <p className={`${bodySize} text-white/80`}>
                Alle Nichttextelemente erhalten ein Textäquivalent — beispielsweise Alternativtexte für Bilder
                oder Transkripte für Videos.
              </p>
            </div>

            <div>
              <h3 className={`${bodySize} font-semibold mb-2`}>Kontinuierliche Prüfung</h3>
              <p className={`${bodySize} text-white/80`}>
                Wir prüfen unsere Website regelmäßig anhand verschiedener Methoden: Browser-Plugins,
                Evaluierungen durch externe Anbieter sowie individuelle Tests. Dieser fortlaufende Prozess
                stellt sicher, dass unsere Seiten auch bei sich ändernden Leitlinien und neuen Technologien
                barrierefrei zugänglich bleiben.
              </p>
            </div>
          </div>
        </div>

        {/* Feedback */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>Feedback & Kontakt</h2>
          <div className="h-px bg-white/20 mb-6" />
          <p className={`${bodySize} text-white/80 mb-4`}>
            Uns ist bewusst, dass einzelne Seiten Unzulänglichkeiten aufweisen können. Sollten Sie
            Barrieren feststellen oder Vorschläge zur Verbesserung der Zugänglichkeit haben,
            kontaktieren Sie uns bitte:
          </p>
          <p className={`${bodySize} text-white/90 font-semibold`}>
            E-Mail: info@estata.com<br />
            Telefon: +49 30 6298 7730
          </p>
        </div>

        {/* Unser Engagement */}
        <div className="mb-12">
          <h2 className={`${h2Size} font-bold mb-6`}>Unser Engagement</h2>
          <div className="h-px bg-white/20 mb-6" />
          <p className={`${bodySize} text-white/80 mb-4`}>
            ESTATA misst dem Thema Barrierefreiheit nicht nur bei der Gestaltung neuer Seiten eine große
            Bedeutung zu, sondern lässt es vorausschauend in Alltagstechnologien und die Gestaltung aller
            Inhalte einfließen.
          </p>
          <p className={`${bodySize} text-white/80 mb-4`}>
            Unser Team setzt sich für ein Arbeitsumfeld ein, das für alle Beschäftigten — persönlich wie
            beruflich — ein Gefühl der Zugehörigkeit schafft und eine Kultur der Gleichstellung fördert.
            Ein wesentlicher Bestandteil dieses Bestrebens ist es, einen barrierefreien Arbeitsplatz zu
            etablieren, an dem sichtbare und unsichtbare Hindernisse abgebaut werden.
          </p>
          <p className={`${bodySize} text-white/90 font-semibold`}>
            Bei ESTATA arbeiten wir jeden Tag daran, Barrieren abzubauen.
          </p>
        </div>

        <div className="h-px bg-white/20 mb-6" />
        <p className={`${bodySize} text-white/40`}>
          Stand: März 2026 · ESTATA GmbH, Katharinenstraße 9, 10711 Berlin
        </p>
      </Container>
    </section>
  );
};

export default AccessibilityStatement;
